/**
 * CRM-side Visa Probability widget — embedded on the Application Detail page.
 * Auto-fetches the student's profile and runs the predictor on demand. Saves
 * the result to db.visa_assessments so counsellors can revisit it later.
 */
import { useState, useCallback, useEffect } from 'react';
import { Sparkles, Loader2, AlertTriangle, Trophy, ChevronRight, Globe2, Info, ClipboardList } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { visaProbabilityAPI } from '@/lib/api';

const COMPLETENESS_COLOR = {
  Complete: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  Partial: 'bg-amber-100 text-amber-800 border-amber-300',
};

const COLOR_BG = {
  emerald: 'bg-emerald-50 border-emerald-300 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200',
  amber: 'bg-amber-50 border-amber-300 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200',
  orange: 'bg-orange-50 border-orange-300 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200',
  rose: 'bg-rose-50 border-rose-300 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200',
};

const SEVERITY = {
  critical: 'bg-rose-100 text-rose-800 border-rose-300',
  high: 'bg-orange-100 text-orange-800 border-orange-300',
  moderate: 'bg-amber-100 text-amber-800 border-amber-300',
  info: 'bg-sky-100 text-sky-800 border-sky-300',
};

export default function VisaProbabilityWidget({ studentId, applicationId }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [missingFields, setMissingFields] = useState([]);
  const [checkingFields, setCheckingFields] = useState(true);
  const [formValues, setFormValues] = useState({});
  const [saving, setSaving] = useState(false);

  const loadMissingFields = useCallback(async () => {
    if (!studentId) {
      setCheckingFields(false);
      return;
    }
    setCheckingFields(true);
    try {
      const { data } = await visaProbabilityAPI.getMissingFields(studentId);
      setMissingFields(data.missing_fields || []);
    } catch (err) {
      // Non-fatal — the widget still works via "Run prediction", it just
      // won't proactively prompt for gaps if this lookup fails.
      setMissingFields([]);
    } finally {
      setCheckingFields(false);
    }
  }, [studentId]);

  useEffect(() => {
    loadMissingFields();
  }, [loadMissingFields]);

  const run = useCallback(async (overrides = {}) => {
    setLoading(true);
    try {
      const { data } = await visaProbabilityAPI.predictCRM({
        overrides: { ...(studentId ? { student_id: studentId } : {}), ...overrides },
        application_id: applicationId || undefined,
        include_advisory: true,
        depth: 'auto',
      });
      setResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not run prediction');
    } finally {
      setLoading(false);
    }
  }, [studentId, applicationId]);

  const saveAndRun = useCallback(async () => {
    if (!studentId) {
      run();
      return;
    }
    setSaving(true);
    try {
      // Only send fields the counsellor actually filled in.
      const payload = Object.fromEntries(
        Object.entries(formValues).filter(([, v]) => v !== '' && v !== undefined)
      );
      if (Object.keys(payload).length > 0) {
        await visaProbabilityAPI.updateStudentVisaProfile(studentId, payload);
      }
      await loadMissingFields();
      await run(payload);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save profile data');
    } finally {
      setSaving(false);
    }
  }, [studentId, formValues, run, loadMissingFields]);

  return (
    <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-5 mb-6" data-testid="visa-probability-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center text-white">
            <Sparkles className="w-4 h-4" />
          </div>
          <h3 className="font-semibold text-gray-800 dark:text-white">Visa Success Probability</h3>
          <Badge variant="outline" className="text-xs">AI · GPT-5.2</Badge>
        </div>
        {missingFields.length === 0 && (
          <Button
            size="sm"
            onClick={() => run()}
            disabled={loading}
            className="bg-teal-600 hover:bg-teal-700 text-white"
            data-testid="run-visa-prediction"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
            {result ? 'Re-run' : 'Run prediction'}
          </Button>
        )}
      </div>

      {checkingFields && (
        <p className="text-sm text-gray-500 dark:text-gray-400">Checking profile completeness…</p>
      )}

      {!checkingFields && missingFields.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-4 space-y-3" data-testid="visa-missing-fields-form">
          <div className="flex items-start gap-2">
            <ClipboardList className="w-4 h-4 text-amber-600 dark:text-amber-300 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-800 dark:text-amber-200">
              This student's profile is missing {missingFields.length} field{missingFields.length === 1 ? '' : 's'} the predictor needs for an accurate result. Fill them in — they'll be saved to the student's profile.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {missingFields.map((f) => (
              <div key={f.key} className="space-y-1">
                <Label className="text-xs">{f.label}</Label>
                {f.type === 'select' ? (
                  <Select value={formValues[f.key] || ''} onValueChange={(v) => setFormValues((s) => ({ ...s, [f.key]: v }))}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      {(f.options || []).map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    type={f.type === 'number' ? 'number' : 'text'}
                    min={f.min}
                    max={f.max}
                    step={f.step}
                    value={formValues[f.key] ?? ''}
                    onChange={(e) => setFormValues((s) => ({ ...s, [f.key]: e.target.value }))}
                    className="h-9 text-sm"
                    data-testid={`visa-field-${f.key}`}
                  />
                )}
              </div>
            ))}
          </div>
          <Button
            size="sm"
            onClick={saveAndRun}
            disabled={saving || loading}
            className="bg-teal-600 hover:bg-teal-700 text-white"
            data-testid="save-and-run-visa-prediction"
          >
            {(saving || loading) ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
            Save & run prediction
          </Button>
        </div>
      )}

      {!checkingFields && missingFields.length === 0 && !result && !loading && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Click <b>Run prediction</b> to score this student across all 10 destinations using their CRM profile + AI advisory.
        </p>
      )}

      {result && (
        <div className="space-y-4 mt-4">
          {/* Data completeness — how much of this score is based on real inputs vs unanswered fields */}
          {result.data_completeness && (
            <div
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs ${COMPLETENESS_COLOR[result.data_completeness.label] || 'bg-rose-100 text-rose-800 border-rose-300'}`}
              data-testid="visa-data-completeness"
            >
              <Info className="w-3.5 h-3.5 shrink-0" />
              <span>
                <b>{result.data_completeness.percent}% data complete</b> ({result.data_completeness.label}).
                {result.data_completeness.missing_fields?.length > 0 &&
                  ` Missing: ${result.data_completeness.missing_fields.join(', ')}.`}
              </span>
            </div>
          )}
          {/* High-risk auto-alert banner */}
          {result.auto_alert && (
            <div className="rounded-lg border-2 border-rose-400 bg-rose-50 dark:bg-rose-900/30 p-3" data-testid="visa-high-risk-alert">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-rose-600 dark:text-rose-300 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="font-semibold text-rose-700 dark:text-rose-200">
                    High-risk profile detected — counsellors notified
                  </p>
                  <ul className="mt-1 space-y-0.5 text-xs text-rose-700 dark:text-rose-300">
                    {result.auto_alert.reasons?.map((r, i) => (
                      <li key={i} className="flex gap-1.5">
                        <ChevronRight className="w-3 h-3 mt-0.5 shrink-0" /> {r}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-1.5 text-xs text-rose-600/80 dark:text-rose-300/80">
                    {result.auto_alert.notified_count} team member{result.auto_alert.notified_count === 1 ? '' : 's'} pinged · Application tagged <Badge variant="outline" className="text-[10px] border-rose-400 text-rose-700">needs_review</Badge> for triage.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Top headline */}
          <div className={`rounded-lg border p-3 ${COLOR_BG[result.ranking[0].color]}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Trophy className="w-5 h-5" />
                <div>
                  <p className="text-xs uppercase tracking-wide opacity-70">Best match</p>
                  <p className="font-bold">{result.ranking[0].country} — {result.ranking[0].probability}%</p>
                </div>
              </div>
              <Badge className="bg-white/60 text-gray-800">{result.ranking[0].band}</Badge>
            </div>
            {result.advisory && (
              <p className="text-xs mt-2 leading-relaxed" data-testid="visa-widget-advisory">
                <Sparkles className="inline w-3 h-3 mr-1 -mt-0.5" />
                {result.advisory}
              </p>
            )}
          </div>

          {/* Risk flags */}
          {result.flags?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> Risk flags
              </p>
              <div className="flex flex-wrap gap-1.5">
                {result.flags.map((f) => (
                  <span key={f.id} className={`text-xs px-2 py-1 rounded-full border ${SEVERITY[f.severity] || SEVERITY.info}`} title={f.detail}>
                    {f.label}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Full ranking */}
          <div>
            <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5 flex items-center gap-1">
              <Globe2 className="w-3.5 h-3.5 text-teal-500" /> All destinations ranked
            </p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {result.ranking.map((c, i) => (
                <div key={c.country} className={`rounded-md border px-2 py-1.5 text-xs ${COLOR_BG[c.color]}`}>
                  <span className="opacity-60 mr-1">#{i + 1}</span>
                  <span className="font-semibold">{c.country}</span>
                  <span className="float-right font-bold">{c.probability}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Next actions */}
          {result.next_actions?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5">Recommended next actions</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {result.next_actions.map((na) => (
                  <div key={na.country} className="border border-gray-200 dark:border-slate-700 rounded-md p-2 text-xs">
                    <p className="font-semibold text-teal-600 mb-1">{na.country}</p>
                    {na.actions?.map((a, i) => (
                      <p key={i} className="flex gap-1"><ChevronRight className="w-3 h-3 text-teal-500 shrink-0 mt-0.5" />{a}</p>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
